import { useRef, useState, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { TranscriptSegment } from '@/domain/schema/v1';

export const fmtMs = (ms: number | undefined) => {
  const s = Math.floor((ms ?? 0) / 1000);
  const h = Math.floor(s / 3600);
  const p = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}` : `${p(Math.floor(s / 60))}:${p(s % 60)}`;
};

export function RoleBadge({ seg }: { seg: Pick<TranscriptSegment, 'clinicalRole' | 'roleConfidence' | 'roleSource'> }) {
  const sure = seg.roleSource === 'user' || seg.roleConfidence >= 0.85;
  const label =
    seg.clinicalRole === 'counselor'
      ? '상담자'
      : seg.clinicalRole === 'client'
        ? '내담자'
        : seg.clinicalRole === 'mixed'
          ? '혼합'
          : '불명';
  const cls =
    seg.clinicalRole === 'unknown' || seg.clinicalRole === 'mixed'
      ? 'badge-missing'
      : sure
        ? seg.clinicalRole === 'client'
          ? 'badge-auto'
          : 'badge-user'
        : 'badge-review';
  return (
    <span
      className={`badge ${cls}`}
      title={`신뢰도 ${Math.round(seg.roleConfidence * 100)}%${seg.roleSource === 'user' ? ' · 직접 지정' : ''}`}
    >
      {label}
      {!sure && seg.clinicalRole !== 'unknown' && seg.clinicalRole !== 'mixed' ? '(추정)' : ''}
    </span>
  );
}

/**
 * 화자 직접 지정: 자동 판정(추정 표시 포함)을 보여주고, 상담자 발언 / 내담자 발언 / 모름 중 고르게 한다.
 * 고른 값은 "직접 지정"으로 저장되어 다시 자동 판정에 덮이지 않는다.
 */
export function RolePicker({
  seg,
  onPick,
}: {
  seg: Pick<TranscriptSegment, 'clinicalRole' | 'roleConfidence' | 'roleSource'>;
  onPick: (role: 'counselor' | 'client' | 'unknown') => void;
}) {
  const chosen = seg.roleSource === 'user' ? seg.clinicalRole : null;
  return (
    <span className="role-picker row small">
      <span className="muted">화자</span>
      <RoleBadge seg={seg} />
      {(
        [
          ['counselor', '상담자 발언'],
          ['client', '내담자 발언'],
          ['unknown', '모름'],
        ] as const
      ).map(([r, label]) => (
        <button
          key={r}
          type="button"
          className={`chip ${chosen === r ? 'chip-on' : ''}`}
          aria-pressed={chosen === r}
          onClick={() => onPick(r)}
        >
          {label}
        </button>
      ))}
    </span>
  );
}

/**
 * Working Transcript 보기. 120분 이상도 화면에 보이는 줄만 그린다 (TRANSCRIPT_PROTOCOL §13).
 * 역할 배지를 누르면 직접 고칠 수 있다 (확인을 요구하지 않음).
 */
export function TranscriptViewer(props: {
  segments: TranscriptSegment[];
  onSetRole: (segmentId: string, role: 'counselor' | 'client' | 'unknown') => void;
  /** 검색 결과에서 왔을 때: 이 줄로 이동해 표시 */
  focusId?: string;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const rows = q ? props.segments.filter((s) => s.text.includes(q)) : props.segments;
  // React Compiler 경고: 이 프로젝트는 React Compiler를 쓰지 않으므로 해당 없음
  // eslint-disable-next-line react-hooks/incompatible-library
  const v = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 12,
  });
  const focusIndex = props.focusId && !q ? rows.findIndex((s) => s.segmentId === props.focusId) : -1;
  useEffect(() => {
    if (focusIndex >= 0) v.scrollToIndex(focusIndex, { align: 'center' });
    // 처음 열릴 때 한 번만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIndex]);

  return (
    <div className="stack">
      <input
        aria-label="전사 안에서 찾기"
        placeholder="전사 안에서 찾기"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div ref={parentRef} className="transcript-scroll" data-testid="transcript-scroll">
        <div style={{ height: v.getTotalSize(), position: 'relative', width: '100%' }}>
          {v.getVirtualItems().map((item) => {
            const s = rows[item.index]!;
            return (
              <div
                key={s.segmentId}
                ref={v.measureElement}
                data-index={item.index}
                className={`t-row ${s.isBackchannel ? 'muted' : ''} ${s.verificationStatus === 'needs-review' ? 't-review' : ''} ${s.segmentId === props.focusId ? 't-focus' : ''}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${item.start}px)`,
                }}
              >
                <span className="t-time">{fmtMs(s.startMs)}</span>
                <button
                  type="button"
                  className="link t-role"
                  onClick={() => setEditing(editing === s.segmentId ? null : s.segmentId)}
                >
                  <RoleBadge seg={s} />
                  <span className="t-role-hint" aria-hidden="true">▾</span>
                </button>
                <span className="t-text">{s.text}</span>
                {editing === s.segmentId && (
                  <span className="t-edit">
                    <RolePicker
                      seg={s}
                      onPick={(r) => {
                        props.onSetRole(s.segmentId, r);
                        setEditing(null);
                      }}
                    />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="muted small">
        {rows.length}줄 · 배지를 누르면 상담자/내담자를 직접 고칠 수 있습니다 (필요할 때만). "추정"·"불명" 줄은 내담자
        진술의 자동 근거로 쓰지 않습니다.
      </p>
    </div>
  );
}
